import { App } from 'antd';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';
import { filter, find, get, startsWith } from 'lodash-es';
import { Prompt, generatePath, useHistory, useRouteMatch } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';

import BaseLoggedPage from '../templates/BaseLoggedPage';
import NewQuoteForm from '../forms/shipments/quotes/newQuoteForm';
import { DynamicFormDependenciesProvider } from '../components/forms/dynamic/dynamicFormDependencies';
import useNewQuoteFormState from '../forms/shipments/quotes/useNewQuoteFormState';
import { OverrideSettingsContextProvider } from '../components/domainSpecific/settingsElements';
import { DataStateIndicatorGuard } from '../components/data/dataStateHandlers';
import { FormSanitizationProvider } from '../components/forms/FormSanitizationContext';
import { useAccounts } from '../hooks/data/auth';
import { AccountSelectionDialog } from '../dialogs/shipments/newShipmentDialogs';
import { AsyncCallbackButton } from '../components/data/asyncActionElements';
import { FAIcon } from '../components/adapters/fontAwesomeAdapters';
import { FlexCol, TitleWithExtra } from '../components/layout/layoutElements';
import ProcessingModal from '../components/dialogs/ProcessingModal';
import { CancelLink } from '../forms/shipments/newShipment/newShipmentElements';
import { useNewShipmentElementsStyles } from '../forms/shipments/newShipment/newShipmentElements.styles';
import { PageExitDialogType } from './nav/PageExitDialog';
import routes, { QUOTE_DETAIL_BASE } from '../app/routes';
import {
  GENERATE_QUOTE_ROUTINGS_MUTATION,
  QUOTE_DETAIL_QUERY,
} from '../app/graphql/quoteQueries';
import {
  useQuoteMessage,
  useRetrieveQuoteMessage,
} from '../components/dialogs/QuoteRestrictionsMessage';
import { InnerContentScrollbars } from '../components/layout/InnerContentScrollbars';
import { graphQLToForm } from '../app/data/quoteConversion';
import { useFlashMessageContext } from '../components/dialogs/FlashMessageProvider';
import { formToGraphqlQuoteInput } from '../app/data/newQuoteConversions';
import { newQuoteGuard } from '../components/guards/pageGuards';
import { withPageGuard } from '../components/guards/PageGuard';
import ActiveAccountHeader from '../widgets/ActiveAccountHeader';
import NewQuoteFormSchemaProvider from '../forms/shipments/newShipment/NewQuoteFormSchemaProvider';
import { SimpleConfirmDialog } from '../components/dialogs/SimpleConfirmDialog';
import QuoteRoutingDialog from '../forms/shipments/quotes/QuoteRoutingDialog';
import { useNewQuotePageStyles } from './NewQuotePage.styles';
import { useLayoutElementsStyles } from '../components/layout/layoutElements.styles';

function useErrorHandling() {
  const { errorMessage } = useFlashMessageContext();
  const [errorDetail, setErrorDetail] = useState(null);

  const dismissError = () => {
    setErrorDetail(null);
  };
  const handleSubmitError = err => {
    const errCode = get(err, 'graphQLErrors.0.extensions.apiErrors.0.code');

    // TODO: would be better to handle all errors same way (similar to errorMessage func),
    // but right now that would require reworking dialog component
    if (errCode === '999013')
      setErrorDetail({
        subtitleId: 'book.quote.noFlightsErrorTitle',
        textId: 'book.quote.noFlightsError',
      });
    else if (errCode === '999014')
      setErrorDetail({
        subtitleId: 'book.quote.noResultsForDate',
        textId: 'book.quote.noResultsForDate-text',
      });
    else errorMessage(err);
  };
  return { errorDetail, handleSubmitError, dismissError };
}

const QUOTE_STEPS = {
  FORM_STEP: 0,
  ROUTING_STEP: 1,
};

function useQuoteSubmitState({
  mutation,
  mutationOptions,
  onError,
  onSuccess,
}) {
  const [mutate, { data: mutationData, loading }] = useMutation(
    mutation,
    mutationOptions
  );
  const [currentStep, setCurrentStep] = useState(QUOTE_STEPS.FORM_STEP);

  const goBack = () => setCurrentStep(QUOTE_STEPS.FORM_STEP);

  async function submitForm(values) {
    try {
      const { data } = await mutate({
        variables: {
          input: formToGraphqlQuoteInput(values),
        },
      });

      onSuccess(data, setCurrentStep);
    } catch (err) {
      onError(err);
    }
  }

  return {
    submit: submitForm,
    quote: mutationData?.generateQuoteRoutings,
    loading,
    currentStep,
    goBack,
  };
}

function NewQuoteExitPrompt({ account, skip }) {
  return (
    <Prompt
      when={!!account && !skip}
      message={({ pathname }) =>
        startsWith(pathname, QUOTE_DETAIL_BASE) ||
        startsWith(pathname, routes.quoting.newQuote)
          ? true
          : PageExitDialogType.CANCEL_QUOTE
      }
    />
  );
}

const NewQuoteButtons = ({ onSubmit, buttonProps, messageId }) => (
  <AsyncCallbackButton
    type="primary"
    data-subject="shipment"
    data-action="save"
    callback={onSubmit}
    loadingTextId="buttons.validating"
    {...buttonProps}
  >
    <span>
      <FormattedMessage id={messageId} />
    </span>
    <FAIcon icon="chevron-right" />
  </AsyncCallbackButton>
);

const NewQuotePageInnerPhase2 = () => {
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const { replace } = useHistory();
  const accountsResult = useAccounts();
  const { account, setAccount } = useNewQuoteFormState();

  const quoteEnabledAccounts = filter(
    accountsResult.customerAccounts,
    acc => acc.newQuote?.enabled
  );

  const onSubmitSuccess = (data, setCurrentStep) => {
    setCurrentStep(QUOTE_STEPS.ROUTING_STEP);
    replace(
      generatePath(
        `${routes.quoting.newQuote}/${data?.generateQuoteRoutings?.referenceNo}`
      )
    );
  };

  return (
    <DataStateIndicatorGuard
      queryResult={{
        ...accountsResult,
        data: quoteEnabledAccounts,
      }}
    >
      {() => (
        <>
          <AccountSelectionDialog
            value={account}
            onChange={setAccount}
            customerAccounts={quoteEnabledAccounts}
            titleId="book.newQuote.accountDialog.title"
            filterFunc={acc => !acc?.partner && acc.newQuote?.enabled}
          />
          <QuotePageBase
            allowPhase2
            mutation={GENERATE_QUOTE_ROUTINGS_MUTATION}
            onSubmitSuccess={onSubmitSuccess}
            title={
              <>
                <FormattedMessage id="book.newQuote.title" />
                <CancelLink
                  messageId="book.newQuote.cancelQuote"
                  className={newShipmentElementsStyles.titleCancelLink}
                />
              </>
            }
            initialAccount={account}
            initialValues={{
              origin: {
                address: {
                  country: 'United States',
                },
              },
              destination: {
                address: {
                  country: 'United States',
                },
              },
            }}
          />
        </>
      )}
    </DataStateIndicatorGuard>
  );
};

export const EditQuotePage = () => {
  const match = useRouteMatch();
  const { replace, push } = useHistory();
  const accountsResult = useAccounts();
  const { message } = App.useApp();
  const { formatMessage } = useIntl();
  const { showRetrieveQuoteMessage } = useRetrieveQuoteMessage();
  const { styles } = useNewQuotePageStyles();
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const { referenceNo } = match.params;
  const [formDirty, setFormDirty] = useState(false);

  const quoteDetailResult = useQuery(QUOTE_DETAIL_QUERY, {
    variables: {
      referenceNo,
    },
  });
  const { data: quoteDetail } = quoteDetailResult;
  const formValues = useMemo(
    () => graphQLToForm(quoteDetail?.getCustomerQuote),
    [quoteDetail?.getCustomerQuote]
  );

  const quoteAccount = find(
    accountsResult.customerAccounts,
    acc => acc.number === formValues.account?.number
  );

  const redirectToResult = () => {
    push(
      generatePath(routes.quoting.quoteDetail, {
        referenceNo: quoteDetail?.getCustomerQuote?.referenceNo,
      })
    );
  };

  const onSubmitSuccess = (data, setCurrentStep) => {
    setCurrentStep(QUOTE_STEPS.ROUTING_STEP);
    const mainPath = generatePath(routes.quoting.editQuote, {
      referenceNo,
    });
    replace(
      generatePath(`${mainPath}/${data?.generateQuoteRoutings?.referenceNo}`)
    );
  };

  useEffect(() => {
    let key;
    if (referenceNo) {
      key = showRetrieveQuoteMessage(formatMessage);
    }

    return () => {
      if (key) message.destroy(key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatMessage]);

  return (
    <DataStateIndicatorGuard
      queryResult={{
        ...quoteDetailResult,
        data: quoteDetail,
      }}
    >
      {() => (
        <QuotePageBase
          onSubmitSuccess={onSubmitSuccess}
          mutation={GENERATE_QUOTE_ROUTINGS_MUTATION}
          onSubmitOverride={!formDirty ? redirectToResult : null}
          allowPhase2
          onValuesChange={() => {
            setFormDirty(true);
          }}
          isEditMode
          title={
            <>
              <div>
                <FormattedMessage id="book.quote.reuseQuote" />
                <p style={{ fontSize: 12, lineHeight: 1, margin: 0 }}>
                  <span
                    className={classNames(
                      'text-uppercase',
                      styles.editQuoteLabel
                    )}
                  >
                    <FormattedMessage id="book.quote.quoteId" />
                  </span>{' '}
                  <span className={styles.editQuoteId}>{referenceNo}</span>
                </p>
              </div>
              <CancelLink
                messageId="book.newQuote.cancelQuote"
                className={newShipmentElementsStyles.titleCancelLink}
              />
            </>
          }
          submitMessageId={
            !formDirty ? 'book.newShipment.getQuote' : 'quote.create'
          }
          initialAccount={quoteAccount}
          initialValues={formValues}
        />
      )}
    </DataStateIndicatorGuard>
  );
};

const QuotePageBase = ({
  initialAccount = null,
  initialValues,
  title,
  mutation,
  submitMessageId = 'book.newShipment.getQuote',
  onValuesChange,
  submitDisabled = false,
  isEditMode = false,
  onSubmitOverride,
  onSubmitSuccess,
  allowPhase2 = false,
}) => {
  const { styles } = useNewQuotePageStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const { message } = App.useApp();
  const intl = useIntl();
  const { formatMessage } = intl;
  const { showQuoteMessage } = useQuoteMessage();
  const [messageShown, setMessageShown] = useState(false);
  const { errorDetail, handleSubmitError, dismissError } = useErrorHandling();

  const {
    form,
    refreshValues,
    account,
    setAccount,
    unitSystem,
    setUnitSystem,
    values,
  } = useNewQuoteFormState(initialAccount);

  useEffect(() => {
    if (initialAccount !== account) {
      setAccount(initialAccount);
    }
  }, [initialAccount, setAccount, account]);

  useEffect(() => {
    if (initialValues?.unitSystem && initialValues?.unitSystem !== unitSystem) {
      setUnitSystem(initialValues?.unitSystem);
    }
  }, [initialValues, setUnitSystem, unitSystem]);

  const { submit, quote, currentStep, goBack, loading } = useQuoteSubmitState({
    mutation,
    onError: handleSubmitError,
    onSuccess: onSubmitSuccess,
  });

  useEffect(() => {
    let key;
    if (account && !messageShown && !isEditMode) {
      key = showQuoteMessage(formatMessage);
      setMessageShown(true);
    }

    return () => {
      if (key) message.destroy(key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, formatMessage]);

  const onSubmit = formValues => {
    submit({ ...formValues, account, unitSystem });
  };

  let onSubmitCallback = () => form?.formRef?.current?.triggerSubmit();
  if (onSubmitOverride) onSubmitCallback = onSubmitOverride;

  return (
    <BaseLoggedPage
      id="NewQuotePage"
      className={styles.quotePageBase}
      leftSideContent={<ActiveAccountHeader account={account} />}
    >
      <>
        {account && (
          <NewQuoteFormSchemaProvider account={account}>
            <TitleWithExtra title={title}>
              <div
                className={classNames(
                  'NewShipmentTopControls',
                  newShipmentElementsStyles.newShipmentTopControls,
                  styles.quoteSubmitButton
                )}
              >
                <NewQuoteButtons
                  onSubmit={onSubmitCallback}
                  messageId={submitMessageId}
                  buttonProps={{
                    disabled: submitDisabled,
                  }}
                />
              </div>
            </TitleWithExtra>
            <NewQuoteExitPrompt account={account} />
            <DynamicFormDependenciesProvider values={values}>
              <FlexCol className={layoutStyles.flex1}>
                <OverrideSettingsContextProvider unitSystem={unitSystem}>
                  <FormSanitizationProvider>
                    <InnerContentScrollbars>
                      <NewQuoteForm
                        account={account}
                        onValuesChange={onValuesChange}
                        name="newQuoteForm"
                        form={form?.form}
                        formRef={form?.formRef}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onEditedPackageChange={(changed, allValues) =>
                          requestAnimationFrame(() =>
                            refreshValues({ packages: allValues })
                          )
                        }
                      />
                    </InnerContentScrollbars>
                  </FormSanitizationProvider>
                </OverrideSettingsContextProvider>
              </FlexCol>
              {loading && (
                <ProcessingModal textId="book.newQuote.optimizing-routing" />
              )}
              <SimpleConfirmDialog
                open={!!errorDetail}
                cancelTextId="buttons.dismiss"
                mask
                onClose={dismissError}
                {...errorDetail}
              />
              {allowPhase2 && currentStep === QUOTE_STEPS.ROUTING_STEP && (
                <QuoteRoutingDialog
                  onCancel={goBack}
                  quote={quote}
                  onBackClick={goBack}
                  onError={handleSubmitError}
                />
              )}
            </DynamicFormDependenciesProvider>
          </NewQuoteFormSchemaProvider>
        )}
      </>
    </BaseLoggedPage>
  );
};

export const NewQuotePage = withPageGuard(newQuoteGuard)(
  NewQuotePageInnerPhase2
);
